
import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Edit, Trash2, Plus, Video, FileText, Image } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import type { LearnModule } from '@/types/module'
import { useModuleContent } from '@/hooks/useModuleContent'
import SubmoduleEditDialog from './SubmoduleEditDialog'

interface ModulesTableProps {
  modules: LearnModule[]
  onEdit: (module: LearnModule) => void
  onDelete: (moduleId: string) => Promise<void>
}

const ModulesTable = ({ modules, onEdit, onDelete }: ModulesTableProps) => {
  const [deletingModuleId, setDeletingModuleId] = useState<string | null>(null)
  const [editingSubmodule, setEditingSubmodule] = useState<any>(null)
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null)
  const { toast } = useToast()
  const { 
    moduleContents, 
    loading: contentLoading, 
    fetchContent, 
    updateContent, 
    createContent, 
    deleteContent 
  } = useModuleContent()

  const handleDelete = async (moduleId: string) => {
    try {
      setDeletingModuleId(moduleId)
      console.log('Initiating delete for module:', moduleId)
      await onDelete(moduleId)
      console.log('Delete operation completed successfully')
    } catch (error) {
      console.error('Error in handleDelete:', error)
      toast({
        title: "Error",
        description: "Failed to delete module",
        variant: "destructive",
      })
    } finally {
      setDeletingModuleId(null)
    }
  }

  const handleEdit = (module: LearnModule) => {
    console.log('Initiating edit for module:', module)
    onEdit(module)
  }

  const handleAccordionChange = (moduleId: string) => {
    if (selectedModuleId !== moduleId) {
      setSelectedModuleId(moduleId)
      fetchContent(moduleId)
    } else {
      setSelectedModuleId(null)
    }
  }

  const handleEditSubmodule = (submodule: any) => {
    setEditingSubmodule(submodule)
  }

  const handleCreateSubmodule = (moduleId: string) => {
    setEditingSubmodule({ 
      module_id: moduleId, 
      title: '', 
      description: '', 
      video_url: '', 
      infographic_url: '',
      content_type: 'video',
      order_index: (moduleContents[moduleId]?.length || 0) + 1
    })
  }

  const handleSaveSubmodule = async (submoduleData: any) => {
    try {
      if (submoduleData.id) {
        await updateContent(submoduleData.id, submoduleData)
      } else {
        await createContent(submoduleData)
      }
      if (selectedModuleId) {
        fetchContent(selectedModuleId)
      }
      setEditingSubmodule(null)
      toast({
        title: "Success",
        description: submoduleData.id ? "Submodule updated successfully" : "Submodule created successfully",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save submodule",
        variant: "destructive",
      })
    }
  }

  const handleDeleteSubmodule = async (submoduleId: string) => {
    try {
      await deleteContent(submoduleId)
      if (selectedModuleId) {
        fetchContent(selectedModuleId)
      }
      toast({
        title: "Success",
        description: "Submodule deleted successfully",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete submodule",
        variant: "destructive",
      })
    }
  }

  if (modules.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No modules found. Create your first module to get started.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Accordion type="single" collapsible className="w-full">
        {modules.map((module) => (
          <AccordionItem key={module.id} value={module.id} className="border rounded-lg">
            <AccordionTrigger 
              className="px-4 hover:no-underline"
              onClick={() => handleAccordionChange(module.id)}
            >
              <div className="flex items-center justify-between w-full mr-4">
                <div className="flex items-center gap-4">
                  <span className="font-medium">{module.title}</span>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    module.category === 'Wealth' ? 'bg-green-100 text-green-800' :
                    module.category === 'Self' ? 'bg-blue-100 text-blue-800' :
                    module.category === 'Social' ? 'bg-orange-100 text-orange-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {module.category}
                  </span>
                  <span className="text-sm text-gray-500">{module.coins} coins</span>
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(module)}
                    title="Edit module"
                    className="h-8 w-8 p-0"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        title="Delete module"
                        className="h-8 w-8 p-0 hover:bg-red-50 hover:border-red-200"
                        disabled={deletingModuleId === module.id}
                      >
                        <Trash2 className={`w-4 h-4 ${deletingModuleId === module.id ? 'animate-pulse' : ''}`} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Module</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "<strong>{module.title}</strong>"? 
                          This will also delete all associated content and cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(module.id)}
                          className="bg-red-600 hover:bg-red-700"
                          disabled={deletingModuleId === module.id}
                        >
                          {deletingModuleId === module.id ? 'Deleting...' : 'Delete'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">Submodules</h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCreateSubmodule(module.id)}
                    className="flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Submodule
                  </Button>
                </div>
                
                {contentLoading && selectedModuleId === module.id ? (
                  <div className="text-center py-4">
                    <p className="text-gray-500">Loading submodules...</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {moduleContents[module.id]?.length ? (
                      moduleContents[module.id].map((content: any) => (
                        <div key={content.id} className="border rounded-lg p-3 bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1">
                                {content.content_type === 'video' && <Video className="w-4 h-4 text-blue-600" />}
                                {content.content_type === 'reading' && <FileText className="w-4 h-4 text-green-600" />}
                                {content.content_type === 'infographic' && <Image className="w-4 h-4 text-purple-600" />}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{content.title}</p>
                                <p className="text-xs text-gray-600">{content.description}</p>
                                <div className="flex gap-2 mt-1">
                                  {content.video_url && (
                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Video</span>
                                  )}
                                  {content.infographic_url && (
                                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">Infographic</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditSubmodule(content)}
                                className="h-7 w-7 p-0"
                              >
                                <Edit className="w-3 h-3" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 w-7 p-0 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Submodule</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete "{content.title}"?
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteSubmodule(content.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-gray-500 text-sm">No submodules found. Add your first submodule to get started.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {editingSubmodule && (
        <SubmoduleEditDialog
          submodule={editingSubmodule}
          isOpen={!!editingSubmodule}
          onClose={() => setEditingSubmodule(null)}
          onSave={handleSaveSubmodule}
        />
      )}
    </div>
  )
}

export default ModulesTable
